import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { AddRounded, CloseRounded } from '@material-ui/icons';
import { Alert, Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker } from '../../components/CustomDatePicker';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { RootState } from '../../app/store';
import DocumentInfoCard from '../../components/DocumentInfoCard';
import HelpTooltip from '../../components/HelpTooltip';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import { DAYS_FOR_COMPLETE_REVIEW } from '../../constants';
import { addWeekdays } from '../../functions/addWeekdays';
import { useAppDispatch } from '../../hooks/useAppRudux';
import useMasterDateForDocumentRevision from '../../hooks/useMasterDateForDocumentRevision';
import { useQueryStringNumber } from '../../hooks/useQueryString';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import { PurposeLabel } from '../ProjectDocument/BulkPlanning';
import { ProjectDocumentRevisionWithTempId } from '../ProjectDocument/DocumentCreate';
import { getProjectDocumentAsync } from '../ProjectDocument/projectDocumentSlice';
import { createProjectDocumentRevisionAsync } from './projectDocumentRevisionSlice';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    hoverFocus: {
      border: '2px solid',
      borderColor: theme.palette.background.default,
      borderRadius: 16,
      transition: '0.4s',
      '&:hover': {
        borderColor: theme.palette.primary.main
      }
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    dividerRevision: {
      margin: theme.spacing(3, 0, 2, 0)
    },
    fileNotFound: {
      padding: theme.spacing(12.25, 2),
      textAlign: 'center'
    },
    buttonNewRev: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(2)
    },
    fullWidth: {
      width: '100%'
    },
    padding1: {
      padding: theme.spacing(1)
    },
    marginTopNone: {
      marginTop: 0
    },
    marginRightNone: {
      marginRight: 0
    },
    marginBottom: {
      marginBottom: theme.spacing(2)
    },
    positionRelative: {
      position: 'relative'
    },
    closeIcon: {
      position: 'absolute',
      right: theme.spacing(0.5),
      top: theme.spacing(0.5),
      zIndex: 10
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    }
  })
);

export default function DocumentRevisionCreate() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const documentId = useQueryStringNumber({ query: 'documentId' });

  const { selectedProjectDocument } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const [purposes] = useMasterDateForDocumentRevision();

  const defaultRevision = {
    purpose: null,
    revision: null,
    manHour: null,
    planIssueDate: new Date(),
    planCompletedDate: addWeekdays(new Date(), DAYS_FOR_COMPLETE_REVIEW),
    isAutoApproval: false,
    projectDocumentId: documentId
  };
  const [tempId, setTempId] = useState(1);
  const [revRegis, setRevRegis] = useState<ProjectDocumentRevisionWithTempId[]>(
    [{ ...defaultRevision, tempId: 0 }]
  );
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (documentId) {
      dispatch(getProjectDocumentAsync(documentId));
    }
  }, []);

  function handleDeleteRevision(id: number) {
    const newRevision = revRegis?.filter((revision) => revision.tempId !== id);
    setRevRegis(newRevision);
  }

  function handleChangeValueById(
    id: number,
    fieldName: keyof ProjectDocumentRevisionWithTempId,
    newValue: any
  ) {
    const revisionIndex = revRegis.findIndex(
      (revision) => revision.tempId === id
    );
    const newRevision = { ...revRegis[revisionIndex], [fieldName]: newValue };
    if (fieldName === 'purpose') {
      newRevision.revision = null;
      newRevision.isAutoApproval = false;
    }
    if (fieldName === 'planIssueDate') {
      newRevision.planCompletedDate = addWeekdays(
        newValue,
        DAYS_FOR_COMPLETE_REVIEW
      );
    }
    setRevRegis([
      ...revRegis.slice(0, revisionIndex),
      newRevision,
      ...revRegis.slice(revisionIndex + 1)
    ]);
  }

  async function onSubmitDocumentRevision(
    event: React.FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();
    try {
      setLoading(true);
      const result = await dispatch(
        createProjectDocumentRevisionAsync(revRegis)
      );
      await unwrapResult(result);
      dispatch(
        loadingSlice.actions.success({
          message: t('documentRevisionCreate.messages.revisionsCreated')
        })
      );
      setTimeout(() => {
        history.push(`/documents/${documentId}`);
      }, 1000);
    } catch (error) {
      setLoading(false);
    }
  }

  if (!documentId) {
    return <Redirect to="/projects" />;
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="createRevision"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          documentName: selectedProjectDocument?.name,
          documentId: selectedProjectDocument?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('documentRevisionCreate.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
          {selectedProjectDocument && (
            <DocumentInfoCard document={selectedProjectDocument} />
          )}
        </Grid>
        <Grid item xs={12} md={9}>
          <form onSubmit={onSubmitDocumentRevision}>
            <Paper className={classes.card}>
              <Typography variant="subtitle1" gutterBottom>
                {t('documentRevisionCreate.subtitle')}
              </Typography>
              <Divider className={classes.divider} />
              <Alert className={classes.marginBottom} severity="info">
                {t('documentRevisionCreate.autoCalculateMessage', { days: DAYS_FOR_COMPLETE_REVIEW })}
              </Alert>
              {revRegis.length === 0 ? (
                <div className={classes.fileNotFound}>{t('documentRevisionCreate.messages.revisionNotFound')}</div>
              ) : (
                <Grid container spacing={2}>
                  {revRegis.map((revision) => {
                    const currentRevision = purposes.find(
                      (purpose) => purpose.id === revision.purpose?.id
                    )?.revisions;
                    const revisionList = currentRevision ? currentRevision : [];
                    return (
                      <Grid item xs={12} sm={6} key={revision.tempId}>
                        <Grid
                          className={clsx(
                            classes.padding1,
                            classes.positionRelative,
                            classes.hoverFocus
                          )}
                          container
                          spacing={1}
                          component={Paper}
                          variant="outlined"
                        >
                          <Tooltip title={t('documentRevisionCreate.tooltips.remove') as string} arrow>
                            <IconButton
                              className={classes.closeIcon}
                              size="small"
                              onClick={() =>
                                handleDeleteRevision(revision.tempId)
                              }
                            >
                              <CloseRounded />
                            </IconButton>
                          </Tooltip>
                          <Grid item xs={7}>
                            <Autocomplete
                              className={classes.fullWidth}
                              id={`purpose-${revision.tempId}`}
                              options={purposes}
                              getOptionLabel={(option) =>
                                `${option.abbreviation}: ${option.fullName}`
                              }
                              value={revision.purpose}
                              getOptionSelected={(option, value) =>
                                option.id === value.id
                              }
                              onChange={(event, newValue) =>
                                handleChangeValueById(
                                  revision.tempId,
                                  'purpose',
                                  newValue
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  label={t('documentRevisionCreate.labels.purpose')}
                                  required
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <Autocomplete
                              className={classes.fullWidth}
                              id={`revision-${revision.tempId}`}
                              options={revisionList}
                              getOptionLabel={(option) => option?.name}
                              value={revision.revision}
                              getOptionSelected={(option, value) =>
                                option.id === value.id
                              }
                              onChange={(event, newValue) =>
                                handleChangeValueById(
                                  revision.tempId,
                                  'revision',
                                  newValue
                                )
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  label={t('documentRevisionCreate.labels.revision')}
                                  required
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <TextField
                              className={classes.fullWidth}
                              type="number"
                              id={`man-hour-${revision.tempId}`}
                              size="small"
                              label={t('documentRevisionCreate.labels.manHour')}
                              required
                              value={revision.manHour ? revision.manHour : ''}
                              onChange={(event) =>
                                handleChangeValueById(
                                  revision.tempId,
                                  'manHour',
                                  event.target.value === '' ||
                                    Number.parseFloat(event.target.value) <= 0
                                    ? null
                                    : Number.parseFloat(event.target.value)
                                )
                              }
                            />
                          </Grid>
                          <Grid item xs={7}>
                            <KeyboardDatePicker
                              className={clsx(
                                classes.fullWidth,
                                classes.marginTopNone
                              )}
                              autoOk
                              size="small"
                              variant="inline"
                              InputAdornmentProps={{ position: 'end' }}
                              id={`issue-date-${revision.tempId}`}
                              label="Issue Date"
                              value={revision.planIssueDate}
                              onChange={(event) =>
                                handleChangeValueById(
                                  revision.tempId,
                                  'planIssueDate',
                                  event
                                )
                              }
                              KeyboardButtonProps={{
                                'aria-label': t('documentRevisionCreate.ariaLabels.changeDate') as string
                              }}
                              required
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <KeyboardDatePicker
                              className={clsx(
                                classes.fullWidth,
                                classes.marginTopNone
                              )}
                              autoOk
                              size="small"
                              variant="inline"
                              InputAdornmentProps={{ position: 'end' }}
                              id={`completed-date-${revision.tempId}`}
                              label="Completed Date"
                              value={revision.planCompletedDate}
                              onChange={(event) =>
                                handleChangeValueById(
                                  revision.tempId,
                                  'planCompletedDate',
                                  event
                                )
                              }
                              KeyboardButtonProps={{
                                'aria-label': t('documentRevisionCreate.ariaLabels.changeDate') as string
                              }}
                              required
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              className={classes.marginRightNone}
                              control={
                                <Checkbox
                                  size="small"
                                  color="primary"
                                  disabled={
                                    revision.purpose?.id !== PurposeLabel.IFR
                                  }
                                  checked={revision.isAutoApproval}
                                  onChange={(event) =>
                                    handleChangeValueById(
                                      revision.tempId,
                                      'isAutoApproval',
                                      event.target.checked
                                    )
                                  }
                                />
                              }
                              label={t('documentRevisionCreate.labels.autoApproval')}
                            />
                            <HelpTooltip>
                              {revision.purpose?.id === PurposeLabel.IFR
                                ? t('documentRevisionCreate.helpText.autoApprovalEnabled')
                                : t('documentRevisionCreate.helpText.autoApprovalIfrOnly')}
                            </HelpTooltip>
                          </Grid>
                        </Grid>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
              <Divider className={classes.dividerRevision} />
              <div className={classes.buttonNewRev}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setRevRegis([
                      ...revRegis,
                      { ...defaultRevision, tempId: tempId }
                    ]);
                    setTempId(tempId + 1);
                  }}
                >
                  {t('documentRevisionCreate.actions.newRevision')}
                </Button>
              </div>
            </Paper>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={revRegis.length === 0 || loading}
              startIcon={<AddRounded />}
            >
              {t('documentRevisionCreate.actions.addRevision')}
            </Button>
          </form>
        </Grid>
      </Grid>
    </div>
  );
}
